import * as PIXI from 'pixi.js';
import { Scene } from '../core/Scene';
import type { Game } from '../core/Game';
import { Player } from '../entities/Player';
import { Enemy } from '../entities/Enemy';
import { Coin } from '../entities/Coin';
import { Exit } from '../entities/Exit';
import type { PhishingTrap } from '../entities/PhishingTrap';
import { VisionSystem } from '../systems/VisionSystem';
import { NoiseSystem } from '../systems/NoiseSystem';
import { CombatSystem } from '../systems/CombatSystem';
import { Tutorial } from '../ui/Tutorial';
import { circleVsCircle, circleVsAABB } from '../core/Collision';
import type { AABB } from '../core/Collision';
import { TILE_SIZE } from '@hamsters/game-config';
import type { WeaponType } from '@hamsters/game-config';
import { getLevelConfig } from '@hamsters/game-config/src/levels';
import type { LevelConfig } from '@hamsters/game-config';
import { Vector2 } from '../core/Vector2';
import { getSprite } from '../content/SpriteCache';

export class GameScene extends Scene {
  private player: Player | null = null;
  private enemies: Enemy[] = [];
  private coins: Coin[] = [];
  private traps: PhishingTrap[] = [];
  private exitZone: Exit | null = null;
  private terminals: PIXI.Graphics[] = [];
  private scoreText!: PIXI.Text;
  private fpsText!: PIXI.Text;
  private objectiveText!: PIXI.Text;
  private ammoText!: PIXI.Text;
  private weaponText!: PIXI.Text;
  private tutorial: Tutorial;
  private visionSystem: VisionSystem;
  private noiseSystem: NoiseSystem;
  private combatSystem: CombatSystem;
  private walls: AABB[] = [];
  private collectedCoins = 0;
  private activatedTerminals = 0;
  private totalCoinsRequired = 0;
  private totalTerminalsRequired = 0;
  private isComplete = false;
  private levelConfig: LevelConfig | null = null;
  private currentLevelId = 'level_1';
  private trapTouchCooldown = 0;
  private events: Array<{ type: string; tick: number; data?: Record<string, unknown> }> = [];

  constructor(game: Game) {
    super(game);
    this.tutorial = new Tutorial();
    this.visionSystem = new VisionSystem();
    this.noiseSystem = new NoiseSystem();
    this.combatSystem = new CombatSystem();
  }

  setLevel(levelId: string): void {
    this.currentLevelId = levelId;
  }

  enter(): void {
    const bounds = this.game.getScreenBounds();
    this.collectedCoins = 0;
    this.activatedTerminals = 0;
    this.isComplete = false;
    this.events = [];
    this.traps = [];
    this.terminals = [];
    this.walls = [];

    // Load level config
    this.levelConfig = getLevelConfig(this.currentLevelId) ?? getLevelConfig('level_1')!;
    this.totalCoinsRequired = this.levelConfig.objective.type === 'collect_coins'
      ? this.levelConfig.objective.target
      : 999;
    this.totalTerminalsRequired = this.levelConfig.objective.type === 'activate_terminals'
      ? this.levelConfig.objective.target
      : 0;

    // Setup weapon
    this.combatSystem.clear();
    this.noiseSystem.clear();
    if (this.levelConfig.startingWeapon) {
      this.combatSystem.setWeapon(this.levelConfig.startingWeapon as WeaponType);
      this.combatSystem.setAmmo(this.levelConfig.startingAmmo);
    }

    // Create map
    this.createMap();

    // Create player
    this.player = new Player();
    this.player.position.set(this.levelConfig.playerSpawn.x, this.levelConfig.playerSpawn.y);
    this.player.sprite.x = this.levelConfig.playerSpawn.x;
    this.player.sprite.y = this.levelConfig.playerSpawn.y;
    this.camera.container.addChild(this.player.sprite);
    this.combatSystem.setParentContainer(this.camera.container);

    // Show gun if player has weapon
    if (this.levelConfig.startingWeapon) {
      this.player.setWeapon(true);
    }

    // Create enemies
    for (const enemyConfig of this.levelConfig.enemies) {
      const patrolPath = enemyConfig.patrolPath?.map((p) => new Vector2(p.x, p.y)) ?? [];
      const enemy = new Enemy(enemyConfig.type, patrolPath);
      enemy.position.set(enemyConfig.position.x, enemyConfig.position.y);
      enemy.sprite.x = enemyConfig.position.x;
      enemy.sprite.y = enemyConfig.position.y;
      enemy.setParentContainer(this.camera.container);
      enemy.setWalls(this.walls);
      this.camera.container.addChild(enemy.sprite);
      this.enemies.push(enemy);
    }

    // Create coins
    this.coins = [];
    for (const lootConfig of this.levelConfig.loot) {
      const coin = new Coin(lootConfig.type, lootConfig.value ?? 10);
      coin.position.set(lootConfig.position.x, lootConfig.position.y);
      coin.setBaseY(lootConfig.position.y);
      coin.sprite.x = lootConfig.position.x;
      coin.sprite.y = lootConfig.position.y;
      this.camera.container.addChild(coin.sprite);
      this.coins.push(coin);
    }

    // Create exit
    this.exitZone = new Exit();
    this.exitZone.position.set(896, 896);
    this.exitZone.sprite.x = 896;
    this.exitZone.sprite.y = 896;
    this.camera.container.addChild(this.exitZone.sprite);

    // Create terminals (for level 3+)
    if (this.levelConfig.objective.type === 'activate_terminals') {
      this.createTerminals();
    }

    // Setup camera
    const mapSize = this.currentLevelId === 'level_5' ? 1536 : 960;
    this.camera.setBounds(mapSize, mapSize);

    // Setup input
    if (this.input) {
      this.input.setupVirtualJoystick();
    }

    // Setup vision
    this.visionSystem.setWalls(this.walls);

    // Tutorial
    this.tutorial.setSteps(this.levelConfig.tutorialSteps ?? []);
    if (this.levelConfig.tutorialSteps && this.levelConfig.tutorialSteps.length > 0) {
      this.game.app.stage.addChild(this.tutorial.getContainer());
      this.tutorial.show(0, bounds.width / 2, bounds.height / 2);
    }

    // HUD
    this.createHUD(bounds);

    // Track event
    this.trackEvent('RUN_START');
  }

  private createTerminals(): void {
    const positions = [
      { x: 300, y: 300 },
      { x: 700, y: 500 },
      { x: 500, y: 800 },
    ];

    for (let i = 0; i < this.totalTerminalsRequired && i < positions.length; i++) {
      const pos = positions[i];
      if (!pos) continue;

      const terminal = new PIXI.Graphics();
      terminal.rect(-16, -16, 32, 32);
      terminal.fill(0x0066ff);
      terminal.rect(-12, -12, 24, 24);
      terminal.fill(0x0088ff);
      // Screen
      terminal.rect(-8, -8, 16, 12);
      terminal.fill(0x00ff00);
      terminal.x = pos.x;
      terminal.y = pos.y;

      const label = new PIXI.Text({
        text: `T${i + 1}`,
        style: { fill: 0xffffff, fontSize: 10, fontFamily: 'Arial' },
      });
      label.anchor.set(0.5);
      label.y = -24;
      terminal.addChild(label);

      this.camera.container.addChild(terminal);
      this.terminals.push(terminal);
    }
  }

  fixedUpdate(dt: number, tick: number): void {
    if (!this.player || this.isComplete) return;

    // Update player
    this.player.update(dt, tick, this.input ?? undefined);

    // Dismiss tutorial when player moves
    if (this.tutorial.getIsVisible() && this.player.getSpeed() > 10) {
      this.tutorial.dismiss();
    }

    // Handle attack input
    if (this.input) {
      const inputState = this.input.getState();
      if (inputState.attack && this.combatSystem.hasWeapon()) {
        const fired = this.combatSystem.tryShoot(
          this.player, tick, this.noiseSystem, this.enemies, this.walls,
        );
        if (fired) {
          this.trackEvent('SHOT', tick, {
            weapon: this.combatSystem.getWeaponType(),
            ammoRemaining: this.combatSystem.getAmmo(),
          });
        }
      }
    }

    // Update camera
    this.camera.follow(this.player.position);

    // Update enemies
    for (const enemy of this.enemies) {
      enemy.update(dt, tick);
      enemy.updateTraps(dt);
    }

    // Update traps
    this.traps = this.traps.filter((trap) => {
      if (!trap.isActive()) return false;
      trap.update(dt, tick);
      return true;
    });

    // Update coins
    for (const coin of this.coins) {
      if (coin.active) {
        coin.update(dt, tick);
      }
    }

    // Update exit
    if (this.exitZone) {
      this.exitZone.update(dt, tick);
    }

    // Vision check
    this.visionSystem.update(this.enemies, this.player);

    // Noise system
    this.noiseSystem.update(tick, dt, this.enemies, this.walls);

    // Combat system (projectiles)
    this.combatSystem.update(dt, tick, this.enemies, this.walls);

    // Trap cooldown
    if (this.trapTouchCooldown > 0) {
      this.trapTouchCooldown -= dt * 1000;
    }

    // Collision detection
    this.checkCollisions(tick);

    // Update HUD
    this.fpsText.text = `FPS: ${this.time.fps}`;
    let scoreText = '';
    if (this.levelConfig?.objective.type === 'collect_coins') {
      scoreText = `Монеты: ${this.collectedCoins}/${this.totalCoinsRequired}`;
    } else if (this.levelConfig?.objective.type === 'activate_terminals') {
      scoreText = `Терминалы: ${this.activatedTerminals}/${this.totalTerminalsRequired}`;
    } else {
      scoreText = `Цель: ${this.getObjectiveText()}`;
    }

    // Show secondary objective
    if (this.levelConfig?.secondaryObjective) {
      if (this.levelConfig.secondaryObjective.type === 'find_key') {
        const hasKey = this.coins.some(c => c.getType() === 'QUEST_ITEM' && !c.active);
        scoreText += hasKey ? ' + Ключ найден!' : ' + Найди ключ';
      }
    }

    this.scoreText.text = scoreText;

    // Update ammo display
    if (this.combatSystem.hasWeapon()) {
      const ammo = this.combatSystem.getAmmo();
      this.ammoText.text = `Патроны: ${ammo}`;
      this.ammoText.visible = true;
      this.weaponText.visible = true;
    } else {
      this.ammoText.visible = false;
      this.weaponText.visible = false;
    }
  }

  private getObjectiveText(): string {
    if (!this.levelConfig) return '';
    switch (this.levelConfig.objective.type) {
      case 'find_key': return 'Найди ключ';
      case 'neutralize_targets': return 'Нейтрализуй врагов';
      case 'evacuate': return 'Эвакуируйся';
      default: return 'Выполни задание';
    }
  }

  private checkCollisions(tick: number): void {
    if (!this.player || !this.exitZone) return;

    // Player vs Coins
    for (const coin of this.coins) {
      if (!coin.active) continue;
      if (circleVsCircle(
        { x: this.player.position.x, y: this.player.position.y, radius: this.player.radius },
        { x: coin.position.x, y: coin.position.y, radius: coin.radius },
      )) {
        coin.collect();
        this.collectedCoins++;
        this.trackEvent('LOOT', tick, { coinType: coin.getType(), value: coin.getValue() });
      }
    }

    // Player vs Traps (phishing)
    if (this.trapTouchCooldown <= 0) {
      for (const enemy of this.enemies) {
        for (const trap of enemy.getTraps()) {
          if (!trap.isActive()) continue;
          if (circleVsCircle(
            { x: this.player.position.x, y: this.player.position.y, radius: this.player.radius },
            { x: trap.position.x, y: trap.position.y, radius: trap.radius },
          )) {
            this.onTrapTouched(tick);
            trap['activeTrap'] = false;
            trap['active'] = false;
            trap.sprite.visible = false;
          }
        }
      }
    }

    // Player vs Terminals
    if (this.levelConfig?.objective.type === 'activate_terminals') {
      for (const terminal of this.terminals) {
        if ((terminal as unknown as { activated: boolean }).activated) continue;
        const dx = this.player.position.x - terminal.x;
        const dy = this.player.position.y - terminal.y;
        if (Math.sqrt(dx * dx + dy * dy) < 32) {
          (terminal as unknown as { activated: boolean }).activated = true;
          terminal.tint = 0x88ff88;
          this.activatedTerminals++;
          this.trackEvent('OBJECTIVE', tick, { type: 'terminal', count: this.activatedTerminals });
        }
      }
    }

    // Player vs Enemies (death)
    for (const enemy of this.enemies) {
      if (!enemy.isAlive()) continue;
      if (circleVsCircle(
        { x: this.player.position.x, y: this.player.position.y, radius: this.player.radius },
        { x: enemy.position.x, y: enemy.position.y, radius: enemy.radius },
      )) {
        this.onPlayerCaught(tick);
        return;
      }
    }

    // Player vs Walls
    for (const wall of this.walls) {
      if (circleVsAABB(
        { x: this.player.position.x, y: this.player.position.y, radius: this.player.radius },
        wall,
      )) {
        this.resolveWallCollision(wall);
      }
    }

    // Check win condition
    let objectiveComplete = false;
    if (this.levelConfig?.objective.type === 'collect_coins') {
      objectiveComplete = this.collectedCoins >= this.totalCoinsRequired;
    } else if (this.levelConfig?.objective.type === 'activate_terminals') {
      objectiveComplete = this.activatedTerminals >= this.totalTerminalsRequired;
    } else if (this.levelConfig?.objective.type === 'find_key') {
      objectiveComplete = this.collectedCoins > 0;
    }

    // Check secondary objective
    if (objectiveComplete && this.levelConfig?.secondaryObjective) {
      if (this.levelConfig.secondaryObjective.type === 'find_key') {
        const hasKey = this.coins.some(c => c.getType() === 'QUEST_ITEM' && !c.active);
        objectiveComplete = hasKey;
      } else if (this.levelConfig.secondaryObjective.type === 'collect_coins') {
        objectiveComplete = this.collectedCoins >= this.levelConfig.secondaryObjective.target;
      }
    }

    // Update exit door appearance
    if (this.exitZone) {
      this.exitZone.setObjectiveComplete(objectiveComplete);
    }

    // Player vs Exit
    if (objectiveComplete) {
      if (circleVsCircle(
        { x: this.player.position.x, y: this.player.position.y, radius: this.player.radius },
        { x: this.exitZone.position.x, y: this.exitZone.position.y, radius: this.exitZone.radius },
      )) {
        this.onLevelComplete(tick);
      }
    }
  }

  private onTrapTouched(tick: number): void {
    this.trapTouchCooldown = 2000;
    this.trackEvent('BOOST_USED', tick, { type: 'slow', duration: 2000 });
    if (this.player) {
      const origSpeed = 230;
      (this.player as unknown as { speed: number }).speed = origSpeed * 0.65;
      setTimeout(() => {
        if (this.player) {
          (this.player as unknown as { speed: number }).speed = origSpeed;
        }
      }, 2000);
    }
  }

  private resolveWallCollision(wall: AABB): void {
    if (!this.player) return;

    const px = this.player.position.x;
    const py = this.player.position.y;
    const pr = this.player.radius;

    const closestX = Math.max(wall.x, Math.min(px, wall.x + wall.width));
    const closestY = Math.max(wall.y, Math.min(py, wall.y + wall.height));

    const dx = px - closestX;
    const dy = py - closestY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < pr && dist > 0) {
      const overlap = pr - dist;
      this.player.position.x += (dx / dist) * overlap;
      this.player.position.y += (dy / dist) * overlap;
    }
  }

  private onPlayerCaught(tick: number): void {
    if (this.isComplete) return;
    this.trackEvent('PLAYER_CAUGHT', tick);
    this.game.events.emit('player:death');
    this.showGameOver();
  }

  private onLevelComplete(tick: number): void {
    if (this.isComplete) return;
    this.isComplete = true;
    this.trackEvent('RUN_FINISH', tick);
    this.game.events.emit('level:complete');
    this.showVictory();
  }

  private showGameOver(): void {
    const bounds = this.game.getScreenBounds();
    const scale = Math.min(bounds.width, bounds.height) / 600;

    const overlay = new PIXI.Graphics();
    overlay.rect(0, 0, bounds.width, bounds.height);
    overlay.fill({ color: 0x000000, alpha: 0.8 });
    overlay.eventMode = 'static';
    this.game.app.stage.addChild(overlay);

    const title = new PIXI.Text({
      text: 'ПОЙМАН!',
      style: { fill: 0xff4444, fontSize: Math.round(48 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    title.anchor.set(0.5);
    title.x = bounds.width / 2;
    title.y = bounds.height * 0.35;
    overlay.addChild(title);

    const msg = new PIXI.Text({
      text: 'Скамер поймал тебя!\nПопробуй ещё раз',
      style: { fill: 0xffffff, fontSize: Math.round(24 * scale), fontFamily: 'Arial', align: 'center' },
    });
    msg.anchor.set(0.5);
    msg.x = bounds.width / 2;
    msg.y = bounds.height * 0.5;
    overlay.addChild(msg);

    const btnW = Math.round(180 * scale);
    const btnH = Math.round(50 * scale);
    const retryBtn = new PIXI.Container();
    const retryBg = new PIXI.Graphics();
    retryBg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 12);
    retryBg.fill(0x4CAF50);
    retryBtn.addChild(retryBg);
    const retryText = new PIXI.Text({
      text: 'ЗАНОВО',
      style: { fill: 0xffffff, fontSize: Math.round(22 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    retryText.anchor.set(0.5);
    retryBtn.addChild(retryText);
    retryBtn.x = bounds.width / 2;
    retryBtn.y = bounds.height * 0.65;
    retryBtn.eventMode = 'static';
    retryBtn.cursor = 'pointer';
    retryBtn.on('pointerdown', () => {
      this.game.switchScene('game');
    });
    overlay.addChild(retryBtn);
  }

  private showVictory(): void {
    const bounds = this.game.getScreenBounds();
    const scale = Math.min(bounds.width, bounds.height) / 600;

    const overlay = new PIXI.Graphics();
    overlay.rect(0, 0, bounds.width, bounds.height);
    overlay.fill({ color: 0x000000, alpha: 0.8 });
    overlay.eventMode = 'static';
    this.game.app.stage.addChild(overlay);

    const title = new PIXI.Text({
      text: 'УРОВЕНЬ ПРОЙДЕН!',
      style: { fill: 0x4CAF50, fontSize: Math.round(42 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    title.anchor.set(0.5);
    title.x = bounds.width / 2;
    title.y = bounds.height * 0.35;
    overlay.addChild(title);

    let victoryText = 'Задание выполнено!';
    if (this.levelConfig?.objective.type === 'collect_coins') {
      victoryText = `Монеты: ${this.collectedCoins}/${this.totalCoinsRequired}`;
    } else if (this.levelConfig?.objective.type === 'activate_terminals') {
      victoryText = `Терминалы: ${this.activatedTerminals}/${this.totalTerminalsRequired}`;
    } else if (this.levelConfig?.objective.type === 'find_key') {
      victoryText = 'Ключ найден!';
    }

    const msg = new PIXI.Text({
      text: victoryText,
      style: { fill: 0xffd700, fontSize: Math.round(28 * scale), fontFamily: 'Arial' },
    });
    msg.anchor.set(0.5);
    msg.x = bounds.width / 2;
    msg.y = bounds.height * 0.5;
    overlay.addChild(msg);

    const btnW = Math.round(180 * scale);
    const btnH = Math.round(50 * scale);
    const nextBtn = new PIXI.Container();
    const nextBg = new PIXI.Graphics();
    nextBg.roundRect(-btnW / 2, -btnH / 2, btnW, btnH, 12);
    nextBg.fill(0x4CAF50);
    nextBtn.addChild(nextBg);
    const nextText = new PIXI.Text({
      text: 'ДАЛЬШЕ',
      style: { fill: 0xffffff, fontSize: Math.round(22 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    nextText.anchor.set(0.5);
    nextBtn.addChild(nextText);
    nextBtn.x = bounds.width / 2;
    nextBtn.y = bounds.height * 0.65;
    nextBtn.eventMode = 'static';
    nextBtn.cursor = 'pointer';
    nextBtn.on('pointerdown', () => {
      const nextId = this.getNextLevelId();
      if (nextId) {
        const scene = this.game.scenes.get('game') as GameScene;
        scene.setLevel(nextId);
        this.game.switchScene('game');
      } else {
        this.game.switchScene('menu');
      }
    });
    overlay.addChild(nextBtn);
  }

  private getNextLevelId(): string | null {
    const levels = [
      'level_1', 'level_2', 'level_3', 'level_4', 'level_5',
      'level_6', 'level_7', 'level_8', 'level_9', 'level_10',
      'boss_1',
    ];
    const idx = levels.indexOf(this.currentLevelId);
    if (idx >= 0 && idx < levels.length - 1) {
      return levels[idx + 1] ?? null;
    }
    return null;
  }

  private trackEvent(type: string, tick = 0, data?: Record<string, unknown>): void {
    this.events.push({ type, tick, data });
  }

  private createHUD(bounds: { width: number; height: number; top: number; bottom: number }): void {
    const scale = Math.min(bounds.width, bounds.height) / 600;

    this.scoreText = new PIXI.Text({
      text: '',
      style: { fill: 0xffd700, fontSize: Math.round(20 * scale), fontFamily: 'Arial', align: 'center' },
    });
    this.scoreText.anchor.set(0.5, 0);
    this.scoreText.x = bounds.width / 2;
    this.scoreText.y = bounds.top + 10;
    this.game.app.stage.addChild(this.scoreText);

    this.fpsText = new PIXI.Text({
      text: 'FPS: 0',
      style: { fill: 0x888888, fontSize: Math.round(14 * scale), fontFamily: 'Arial' },
    });
    this.fpsText.x = 10;
    this.fpsText.y = bounds.top + 35;
    this.game.app.stage.addChild(this.fpsText);

    // Weapon indicator
    this.weaponText = new PIXI.Text({
      text: '',
      style: { fill: 0xff6666, fontSize: Math.round(16 * scale), fontFamily: 'Arial' },
    });
    this.weaponText.x = 10;
    this.weaponText.y = bounds.top + 55;
    this.weaponText.visible = false;
    this.game.app.stage.addChild(this.weaponText);

    // Ammo indicator
    this.ammoText = new PIXI.Text({
      text: '',
      style: { fill: 0xffaa00, fontSize: Math.round(18 * scale), fontFamily: 'Arial', fontWeight: 'bold' },
    });
    this.ammoText.x = 10;
    this.ammoText.y = bounds.top + 75;
    this.ammoText.visible = false;
    this.game.app.stage.addChild(this.ammoText);

    // Objective
    const objText = this.levelConfig?.objective.type === 'collect_coins'
      ? `Собери ${this.totalCoinsRequired} монет`
      : this.levelConfig?.objective.type === 'activate_terminals'
        ? `Активируй ${this.totalTerminalsRequired} терминала`
        : 'Выполни задание';

    this.objectiveText = new PIXI.Text({
      text: objText,
      style: { fill: 0xffffff, fontSize: Math.round(16 * scale), fontFamily: 'Arial' },
    });
    this.objectiveText.x = bounds.width / 2 - 100 * scale;
    this.objectiveText.y = bounds.height - 40 - bounds.bottom;
    this.game.app.stage.addChild(this.objectiveText);

    // Pause button
    const pauseBtn = new PIXI.Text({
      text: '⏸',
      style: { fontSize: Math.round(28 * scale), fill: 0xffffff },
    });
    pauseBtn.x = bounds.width - 40;
    pauseBtn.y = bounds.top + 10;
    pauseBtn.eventMode = 'static';
    pauseBtn.cursor = 'pointer';
    pauseBtn.on('pointerdown', () => {
      if (this.game.isPaused) {
        this.game.resume();
      } else {
        this.game.pause();
      }
    });
    this.game.app.stage.addChild(pauseBtn);
  }

  private createMap(): void {
    const mapContainer = new PIXI.Container();
    const isLevel5 = this.currentLevelId === 'level_5';
    const mapSize = isLevel5 ? 1536 : 960;

    // Ground
    const ground = new PIXI.Graphics();
    ground.rect(0, 0, mapSize, mapSize);
    ground.fill(0x2d5a1e);
    mapContainer.addChild(ground);

    // Grid pattern
    const grid = new PIXI.Graphics();
    for (let x = 0; x <= mapSize; x += TILE_SIZE) {
      grid.moveTo(x, 0);
      grid.lineTo(x, mapSize);
    }
    for (let y = 0; y <= mapSize; y += TILE_SIZE) {
      grid.moveTo(0, y);
      grid.lineTo(mapSize, y);
    }
    grid.stroke({ color: 0x1a4a0e, width: 1, alpha: 0.3 });
    mapContainer.addChild(grid);

    // Walls
    const wallData: Array<{ x: number; y: number; width: number; height: number }> = [];

    if (isLevel5) {
      // Level 5: larger map (48x64 tiles = 1536x2048)
      // Outer walls
      wallData.push(
        { x: 0, y: 0, width: mapSize, height: TILE_SIZE },
        { x: 0, y: mapSize - TILE_SIZE, width: mapSize, height: TILE_SIZE },
        { x: 0, y: 0, width: TILE_SIZE, height: mapSize },
        { x: mapSize - TILE_SIZE, y: 0, width: TILE_SIZE, height: mapSize },
      );
      // Interior walls
      wallData.push(
        { x: 576, y: 320, width: 128, height: TILE_SIZE },
        { x: 800, y: 480, width: TILE_SIZE, height: 256 },
        { x: 1024, y: 320, width: 128, height: TILE_SIZE },
        { x: 448, y: 896, width: TILE_SIZE, height: 256 },
        { x: 768, y: 1024, width: 256, height: TILE_SIZE },
        { x: 1152, y: 896, width: TILE_SIZE, height: 256 },
        { x: 320, y: 1280, width: 256, height: TILE_SIZE },
        { x: 896, y: 1408, width: TILE_SIZE, height: 256 },
        { x: 1152, y: 1280, width: 256, height: TILE_SIZE },
        { x: 640, y: 1664, width: 128, height: TILE_SIZE },
        { x: 1024, y: 1664, width: 128, height: TILE_SIZE },
      );
    } else {
      // Levels 1-4: 960x960 map with isolated barriers
      // Outer walls
      wallData.push(
        { x: 0, y: 0, width: mapSize, height: TILE_SIZE },
        { x: 0, y: mapSize - TILE_SIZE, width: mapSize, height: TILE_SIZE },
        { x: 0, y: 0, width: TILE_SIZE, height: mapSize },
        { x: mapSize - TILE_SIZE, y: 0, width: TILE_SIZE, height: mapSize },
      );
      // Isolated floating barriers (each stands alone, min 100px from any wall)
      wallData.push(
        { x: 350, y: 200, width: 96, height: 28 },
        { x: 600, y: 350, width: 28, height: 96 },
        { x: 250, y: 500, width: 96, height: 28 },
        { x: 500, y: 550, width: 28, height: 96 },
        { x: 700, y: 250, width: 96, height: 28 },
        { x: 400, y: 700, width: 96, height: 28 },
      );

      // Vision-blocking bushes (no collision)
      if (this.currentLevelId !== 'level_1') {
        const bushTexture = getSprite('bush');
        const bushPositions = [
          { x: 380, y: 430 }, { x: 580, y: 430 }, { x: 480, y: 620 },
          { x: 250, y: 350 }, { x: 700, y: 350 },
        ];
        for (const pos of bushPositions) {
          if (bushTexture) {
            const bushSprite = new PIXI.Sprite(bushTexture);
            bushSprite.anchor.set(0.5);
            bushSprite.width = 48;
            bushSprite.height = 48;
            bushSprite.x = pos.x;
            bushSprite.y = pos.y;
            mapContainer.addChild(bushSprite);
          } else {
            const bushGfx = new PIXI.Graphics();
            bushGfx.circle(pos.x, pos.y, 24);
            bushGfx.fill(0x228b22);
            mapContainer.addChild(bushGfx);
          }
        }
      }
    }

    const wallsGfx = new PIXI.Graphics();
    for (const w of wallData) {
      wallsGfx.rect(w.x, w.y, w.width, w.height);
      wallsGfx.fill(0x8b4513);
      this.walls.push({ x: w.x, y: w.y, width: w.width, height: w.height });
    }
    mapContainer.addChild(wallsGfx);

    // Decorations - trees
    const trees = new PIXI.Graphics();
    if (isLevel5) {
      const treePos = [
        { x: 100, y: 100 }, { x: 1400, y: 100 },
        { x: 100, y: 1900 }, { x: 1400, y: 1900 },
        { x: 750, y: 100 }, { x: 100, y: 750 },
        { x: 1400, y: 750 }, { x: 750, y: 1900 },
      ];
      for (const pos of treePos) {
        trees.circle(pos.x, pos.y, 16);
        trees.fill(0x228b22);
      }
    } else {
      const treePos = [
        { x: 100, y: 100 }, { x: 850, y: 100 },
        { x: 100, y: 850 }, { x: 850, y: 850 },
        { x: 480, y: 100 }, { x: 100, y: 480 },
      ];
      for (const pos of treePos) {
        trees.circle(pos.x, pos.y, 16);
        trees.fill(0x228b22);
      }
    }
    mapContainer.addChild(trees);

    // Path
    const path = new PIXI.Graphics();
    path.rect(128, 128, 64, 64);
    path.fill({ color: 0x8b7355, alpha: 0.5 });
    if (isLevel5) {
      // Exit is at bottom-right of large map
      path.rect(mapSize - 128, mapSize - 128, 64, 64);
      path.fill({ color: 0x8b7355, alpha: 0.5 });
      // Update exit position for level 5
      if (this.exitZone) {
        this.exitZone.position.set(mapSize - 96, mapSize - 96);
        this.exitZone.sprite.x = mapSize - 96;
        this.exitZone.sprite.y = mapSize - 96;
      }
    } else {
      path.rect(832, 832, 64, 64);
      path.fill({ color: 0x8b7355, alpha: 0.5 });
    }
    mapContainer.addChild(path);

    // Start marker
    const startMarker = new PIXI.Graphics();
    startMarker.circle(this.levelConfig?.playerSpawn.x ?? 160, this.levelConfig?.playerSpawn.y ?? 160, 20);
    startMarker.fill({ color: 0x4CAF50, alpha: 0.3 });
    startMarker.stroke({ color: 0x4CAF50, width: 2 });
    mapContainer.addChild(startMarker);

    // Exit marker
    const exitMarker = new PIXI.Graphics();
    const exitX = isLevel5 ? mapSize - 96 : 896;
    const exitY = isLevel5 ? mapSize - 96 : 896;
    exitMarker.circle(exitX, exitY, 28);
    exitMarker.fill({ color: 0xffd700, alpha: 0.2 });
    exitMarker.stroke({ color: 0xffd700, width: 2, alpha: 0.5 });
    mapContainer.addChild(exitMarker);

    this.camera.container.addChildAt(mapContainer, 0);
  }

  onResize(width: number, height: number): void {
    const bounds = this.game.getScreenBounds();
    const scale = Math.min(width, height) / 600;

    // Reposition HUD elements
    if (this.scoreText) {
      this.scoreText.x = width / 2;
      this.scoreText.y = bounds.top + 10;
      this.scoreText.style.fontSize = Math.round(20 * scale);
    }
    if (this.fpsText) {
      this.fpsText.x = 10;
      this.fpsText.y = bounds.top + 35;
    }
    if (this.weaponText) {
      this.weaponText.x = 10;
      this.weaponText.y = bounds.top + 55;
    }
    if (this.ammoText) {
      this.ammoText.x = 10;
      this.ammoText.y = bounds.top + 75;
    }
    if (this.objectiveText) {
      this.objectiveText.anchor.set(0.5, 0);
      this.objectiveText.x = width / 2;
      this.objectiveText.y = height - 40 - bounds.bottom;
    }
  }

  exit(): void {
    this.entities.clear();
    this.enemies = [];
    this.coins = [];
    this.traps = [];
    this.terminals = [];
    this.combatSystem.clear();
    this.noiseSystem.clear();

    this.camera.container.removeChildren();

    const childrenToRemove: PIXI.Container[] = [];
    for (const child of [...this.game.app.stage.children]) {
      if (child !== this.camera.container) {
        childrenToRemove.push(child);
      }
    }
    for (const child of childrenToRemove) {
      this.game.app.stage.removeChild(child);
      child.destroy({ children: true });
    }

    this.container.removeChildren();
  }
}
